/**
 * Uma linha do card "Preparação do pacote" (E168.F7, GUS-180): o estado com o motivo real e a
 * ação que cabe — a ação que destrava a análise, "Baixar a prévia" na saída com o arquivo gerado e
 * "Retomar" só na saída que falhou. E169.F2: a saída final baixa o arquivo final.
 *
 * E170.F2 (GUS-229): a saída com o arquivo guardado mostra o ponto da jornada (gerado, bloqueado na
 * conferência, aguardando revisão, revisado, assinado, entregue). A bloqueada nunca tem selo
 * verde: nomeia os critérios que não passaram e leva a "Conferir laudo e orientações".
 *
 * As ações da saída (baixar, retomar e, na E172.F19, a prévia em atualização) estão em
 * `AcoesDaSaida`.
 */
import type { AxiosInstance } from 'axios';
import { Link } from 'react-router-dom';

import { enderecoDaMesa } from '@/components/DocumentoDaMesa/CorrigirNaMesa';
import { Badge } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import {
  type EstadoDaLinha,
  type JornadaDaSaida,
  type LinhaDoPacoteData,
  type MotivoDaPreparacao,
  type SaidaDoPacote,
  type TrechoReprovadoData,
} from '@/lib/preparacao_do_pacote';
import { caminhoDaTela, telaDoPasso, type TelaDoPasso } from '@/lib/proximoPasso';
import { ancoraDaSaida } from '@/lib/saidaQueFalhou';
import type { DesfechoDaRetomada } from '@/lib/usePreparacaoDoPacote';

import { AcoesDaSaida } from './AcoesDaSaida';

const t = ptBR.preparacaoDoPacote;
const passos = ptBR.resultadoAuditado.proximoPasso;
const daProntidao: Readonly<Record<string, string>> = ptBR.resultadoAuditado.motivoDaProntidao;
const proprios: Readonly<Record<string, string>> = t.motivo;

type Variante = 'success' | 'info' | 'warning' | 'danger' | 'neutral';

const VARIANTE: Readonly<Record<EstadoDaLinha, Variante>> = {
  pronto: 'success',
  em_andamento: 'info',
  aguardando: 'warning',
  bloqueado: 'danger',
  falhou: 'danger',
  nao_se_aplica: 'neutral',
};

/** E170.F2: só a saída aprovada pelas regras (revisada, assinada, entregue) fica verde. */
const VARIANTE_DA_JORNADA: Readonly<Record<JornadaDaSaida, Variante>> = {
  gerado: 'warning',
  bloqueado: 'danger',
  aguardando_revisao: 'info',
  revisado: 'success',
  assinado: 'success',
  entregue: 'success',
};

const criterios: Readonly<Record<string, string>> = ptBR.conferenciaDasSaidas.criterio;

/** O texto do motivo: o próprio da preparação ou o da prontidão do caso, que a tela já conhece. */
export function textoDoMotivo(motivo: MotivoDaPreparacao): string {
  return proprios[motivo] ?? daProntidao[motivo] ?? motivo;
}

function ehSaida(chave: LinhaDoPacoteData['chave']): chave is SaidaDoPacote {
  return chave === 'exame' || chave === 'riscos';
}

/** O rótulo e a cor: a saída com o arquivo guardado diz o ponto da jornada (E170.F2). */
function selo(linha: LinhaDoPacoteData): { rotulo: string; variante: Variante } {
  if (linha.jornada != null) {
    return { rotulo: t.jornada[linha.jornada], variante: VARIANTE_DA_JORNADA[linha.jornada] };
  }
  return { rotulo: t.estado[linha.estado], variante: VARIANTE[linha.estado] };
}

/** E184.F4 (GUS-145 item 6): o endereço já no trecho que a conferência apontou; E202.F3: a mesa. */
export function enderecoDoTrecho(caseId: string, saida: SaidaDoPacote, trecho: string): string {
  return enderecoDaMesa(caseId, saida, trecho);
}

function TrechoReprovado({
  item,
  saida,
  caseId,
}: {
  item: TrechoReprovadoData;
  saida: SaidaDoPacote | null;
  caseId: string;
}): JSX.Element {
  const regra = criterios[item.chave] ?? item.chave;
  if (item.trecho === '') {
    return (
      <li>
        {t.trechoReprovado.semTrecho
          .replace('{regra}', regra)
          .replace('{no_documento}', item.no_documento)}
      </li>
    );
  }
  return (
    <li>
      {t.trechoReprovado.comTrecho.replace('{regra}', regra).replace('{trecho}', item.trecho)}{' '}
      {saida !== null ? (
        <Link
          className="text-accent-700 underline"
          to={enderecoDoTrecho(caseId, saida, item.trecho)}
          data-testid="pacote-abrir-trecho"
        >
          {t.trechoReprovado.abrir}
        </Link>
      ) : null}
    </li>
  );
}

/** Os critérios que a conferência por regras não aprovou, com o nome da tela de conferência.
 * E184.F4 (GUS-145 item 6): e o trecho que reprovou cada um, com o atalho para o editor. */
function Reprovados({
  linha,
  caseId,
}: {
  linha: LinhaDoPacoteData;
  caseId: string;
}): JSX.Element | null {
  if (linha.reprovados == null || linha.reprovados.length === 0) return null;
  const nomes = linha.reprovados.map((chave) => criterios[chave] ?? chave).join('; ');
  const trechos = linha.trechos_reprovados ?? [];
  const saida = ehSaida(linha.chave) ? linha.chave : null;
  return (
    <div className="text-body-sm text-danger-700" data-testid={`pacote-reprovados-${linha.chave}`}>
      <p>{t.reprovados.replace('{criterios}', nomes)}</p>
      {trechos.length > 0 ? (
        <ul
          className="mt-1 list-disc space-y-1 pl-sc-6"
          data-testid={`pacote-trechos-${linha.chave}`}
        >
          {trechos.map((item) => (
            <TrechoReprovado
              key={`${item.chave}-${item.trecho}`}
              item={item}
              saida={saida}
              caseId={caseId}
            />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** E184.F1 (GUS-145 item 1, ADR-0136 §1): a prévia com edições de uma análise anterior pede a
 * conferência no editor, até a revisão do perito. */
function Herdadas({
  linha,
  caseId,
}: {
  linha: LinhaDoPacoteData;
  caseId: string;
}): JSX.Element | null {
  const n = linha.edicoes_herdadas ?? 0;
  if (!ehSaida(linha.chave) || n === 0) return null;
  return (
    <p className="text-body-sm text-warning-700" data-testid={`pacote-herdadas-${linha.chave}`}>
      {n === 1 ? t.herdadas.uma : t.herdadas.varias.replace('{n}', String(n))}{' '}
      <Link className="text-accent-700 underline" to={enderecoDaMesa(caseId, linha.chave)}>
        {t.herdadas.conferir}
      </Link>
    </p>
  );
}

function contaDosDocumentos(linha: LinhaDoPacoteData): string | null {
  // E216.F3.C03 (GUS-317 4b): com a prévia do exame, a conta da seção 2 ("examinados")
  const [n, textos] =
    linha.examinados != null ? [linha.examinados, t.examinados] : [linha.recebidos, t.recebidos];
  if (n == null) return null;
  return n === 1 ? textos.um : textos.varios.replace('{n}', String(n));
}

function Detalhe({ linha }: { linha: LinhaDoPacoteData }): JSX.Element | null {
  const conta = linha.chave === 'documentos' ? contaDosDocumentos(linha) : null;
  if (conta != null) {
    return <p className="text-body-sm text-fg-muted">{conta}</p>;
  }
  if (linha.estado === 'nao_se_aplica') {
    return <p className="text-body-sm text-fg-muted">{t.naoSeAplica}</p>;
  }
  if (linha.motivo != null) {
    return (
      <p className="text-body-sm text-fg-muted" data-testid={`pacote-motivo-${linha.chave}`}>
        {textoDoMotivo(linha.motivo)}
      </p>
    );
  }
  return null;
}

function Passo({
  linha,
  tela,
  caseId,
}: {
  linha: LinhaDoPacoteData;
  tela: TelaDoPasso;
  caseId: string;
}): JSX.Element | null {
  if (linha.proximo_passo == null) return null;
  // E170.F2: a saída gerada sem aprovação leva à tela onde se confere e se corrige.
  const texto =
    linha.proximo_passo === 'conferir_saida'
      ? t.irParaAConferencia
      : t.passo.replace('{passo}', passos[linha.proximo_passo]);
  const destino = telaDoPasso(linha.proximo_passo);
  if (destino === tela) {
    return (
      <p className="text-body-sm text-fg">{`${texto} ${ptBR.resultadoAuditado.prontidao.nestaTela}`}</p>
    );
  }
  return (
    <Link
      className="text-body-sm text-accent-700"
      to={caminhoDaTela(destino, caseId)}
      data-testid="pacote-passo"
    >
      {texto}
    </Link>
  );
}

export interface LinhaDoPacoteProps {
  client: AxiosInstance;
  caseId: string;
  tela: TelaDoPasso;
  linha: LinhaDoPacoteData;
  onRetomar: (saida: SaidaDoPacote, justificativa: string) => Promise<DesfechoDaRetomada>;
  /** E172.F19: o download encontrou a prévia em atualização; o card é lido de novo. */
  onAtualizar?: (() => void) | undefined;
}

export function LinhaDoPacote({
  client,
  caseId,
  tela,
  linha,
  onRetomar,
  onAtualizar,
}: LinhaDoPacoteProps): JSX.Element {
  return (
    <li
      id={ancoraDaSaida(linha.chave)}
      className="scroll-mt-sc-6 py-sc-3"
      data-testid={`pacote-linha-${linha.chave}`}
      data-estado={linha.estado}
    >
      <div className="flex flex-wrap items-center justify-between gap-sc-2">
        <span className="text-body-md text-fg">{t.linha[linha.chave]}</span>
        <Badge
          variant={selo(linha).variante}
          data-testid={`pacote-estado-${linha.chave}`}
          data-jornada={linha.jornada ?? undefined}
        >
          {selo(linha).rotulo}
        </Badge>
      </div>
      <div className="mt-1 space-y-1">
        <Detalhe linha={linha} />
        <Reprovados linha={linha} caseId={caseId} />
        <Herdadas linha={linha} caseId={caseId} />
        <Passo linha={linha} tela={tela} caseId={caseId} />
        {linha.tentativas > 1 ? (
          <p className="text-caption text-fg-dim">
            {t.tentativas.replace('{n}', String(linha.tentativas))}
          </p>
        ) : null}
        {ehSaida(linha.chave) ? (
          <AcoesDaSaida
            client={client}
            caseId={caseId}
            saida={linha.chave}
            linha={linha}
            onRetomar={onRetomar}
            onAtualizar={onAtualizar}
          />
        ) : null}
      </div>
    </li>
  );
}
